import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import AgencyXLogo from 'images/shared/AgencyXLogo';
import AxBannerPoster from 'images/shared/ax-banner-poster.jpg';
import { motion, AnimatePresence } from 'framer-motion';

const HomepageHeader = () => {
  const [scrollYPosition, setScrollYPosition] = useState(0);
  const [videoVisible, setVideoVisible] = useState(false);

  const videoRef = useRef(null);

  useEffect(() => {
    const handleScrollYPosition = () => setScrollYPosition(window.scrollY);
    window.addEventListener('scroll', handleScrollYPosition);

    const handleVideoCanPlay = () => setVideoVisible(true);
    videoRef.current?.addEventListener('canplay', handleVideoCanPlay);

    return () => {
      window.removeEventListener('scroll', handleScrollYPosition);
      videoRef.current?.removeEventListener('canplay', handleVideoCanPlay);
    };
  }, []);

  return (
    <StyledHeader>
      <HeaderVideo
        ref={videoRef}
        muted
        autoPlay
        loop
        playsInline
        id="bgvid"
        preload="auto"
        controls={false}
        poster={AxBannerPoster}
        aria-hidden="true"
        $videoVisible={videoVisible}>
        <source src="/banner-video.mp4" type="video/mp4" />
      </HeaderVideo>
      <HeaderContent>
        <AnimatePresence>
          <Svg
            key="svg"
            $scrollPosition={scrollYPosition}
            initial={{ opacity: 0, transform: 'translateY(-50px)' }}
            animate={
              scrollYPosition > 0
                ? { opacity: 0, transform: 'translateY(-50px)' }
                : { opacity: 1, transform: 'translateY(0px)' }
            }
            transition={{ delay: 0.25, duration: 0.5 }}
            layout>
            <StyledAgencyXLogo aria-label="Agency X"/>
          </Svg>

          <H1
            key="h1"
            initial={{ opacity: 0, transform: 'scale(0)' }}
            animate={{ opacity: 1, transform: 'scale(1)' }}
            transition={{ delay: 0.5, duration: 0.8 }}>
            Intelligent<br />Healthcare Marketing.
          </H1>
          <P
            key="p"
            initial={{ opacity: 0, transform: 'translateY(50px)' }}
            animate={{ opacity: 1, transform: 'translateY(0px)' }}
            transition={{ delay: 1, duration: 0.8 }}>
            Agency X is an award-winning marketing agency for healthcare, helping brands gain
            competitive advantage by harnessing changes in consumer behaviour, technology, and
            business methods.
          </P>
          <P
            key="p2"
            initial={{ opacity: 0, transform: 'translateY(50px)' }}
            animate={{ opacity: 1, transform: 'translateY(0px)' }}
            transition={{ delay: 1.25, duration: 0.8 }}>
            <strong>
              We are made for progressive healthcare marketers who want to stay ahead of the game.
            </strong>
          </P>
        </AnimatePresence>
      </HeaderContent>
    </StyledHeader>
  );
};

const StyledAgencyXLogo = styled(AgencyXLogo)`
  width: 12rem;

  path {
    fill: white;
    &:nth-child(2) {
      fill: var(--ax-gold);
    }
  }
  @media (min-width: 768px) {
    width: 20rem;
  }
`;

const P = styled(motion.p)`
  font-size: 1rem;
  line-height: 1.4em;
  margin-bottom: 1em;
  color: white;
  text-align: center;
  max-width: 1000px;
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Svg = styled(motion.div)`
  width: auto;
  height: auto;
  opacity: 0;
  margin-bottom: 2rem;
`;

const HeaderVideo = styled.video`
  animation: ${(props) => (props.$videoVisible ? 'fade-in 0.8s ease-in-out forwards' : 'none')};
  background-color: #090a12 !important;
  height: 100%;
  left: 0;
  object-fit: cover;
  object-position: center;
  opacity: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;

  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 0.7;
    }
  }
`;

const H1 = styled(motion.h1)`
  font-family: 'proxima-nova', sans-serif;
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.2;
  color: white;
  margin-bottom: 0.5em;
  text-align: center;
  @media (min-width: 768px) {
    h1 {
      font-size: 4rem;
    }
  }
  @media (min-width: 1150px) {
    font-size: 6.25rem;
  }
`;

const HeaderContent = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  max-width: 1250px;
  position: relative;
  width: 100%;
  z-index: 2;
  padding: 1rem;
  @media (min-width: 600px) {
    padding: 4rem;
  }
  @media (min-width: 1150px) {
    padding: 2rem;
  }
`;

const StyledHeader = styled.header`
  align-items: center;
  background-color: #090a12;
  display: flex;
  height: 100vh;
  justify-content: center;
  min-height: 100%;
  position: relative;
  width: 100%;
  z-index: 0;

  @media (min-width: 1000px) and (max-width: 1300px) and (min-height: 1000px) {
    height: 50vh;
  }
`;

export default HomepageHeader;
